import { formatCurrency, formatNumber, useTranslation } from '@hbs-us/ui';

/**
 * Locale-aware money / percent formatters for the money screens (invoices +
 * estimates) — I18N-05.
 *
 * Clone of the reports `useReportFormatters`, with ONE difference: money is
 * cents-precise (`minimumFractionDigits: 2`) because the per-invoice/estimate
 * figures are not whole-dollar rollups (UI-SPEC Typography note). These wrap the
 * `@hbs-us/ui` `Intl`-based helpers with the ACTIVE i18n locale so the same
 * figures render with locale separators (EN `$1,234.00`, ES `1234,00 $`). The
 * legacy SSR pages hand-rolled an en-US-locked `toLocaleString('en-US', …)` —
 * we must NOT do that (it breaks ES). These wrappers always pass the active
 * locale.
 */
export function useMoneyFormatters() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return {
    formatCurrency: (value: number): string =>
      formatCurrency(value || 0, locale, 'USD', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    /**
     * The cents-side of the same formatter, for the endpoints that carry money as
     * integer cents (clients' `total_cents`). The cents→dollars conversion lives
     * HERE and nowhere else — call sites must never divide by 100 by hand, or the
     * round-trip with the cents-precise API drifts one field at a time.
     */
    formatCents: (cents: number): string =>
      formatCurrency((cents || 0) / 100, locale, 'USD', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    percent: (value: number): string =>
      `${formatNumber(value || 0, locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
  };
}

export type MoneyFormatters = ReturnType<typeof useMoneyFormatters>;
